import { cn } from '@/lib/cn';
import { useScrollRamp } from '../motion/useScrollRamp';

export interface HeroTile {
  label: string;
  /** Texto final; con `number` se cuenta hasta él con la rampa. */
  value: string;
  number?: number | null;
  format?: (n: number) => string;
  hint?: string;
  /** `accent` para el primero; `warn` para lo pendiente o con aviso. */
  tone?: 'default' | 'accent' | 'warn';
  /** De dónde sale la cifra, abajo en pequeño; con `onSource`, enlaza. */
  source?: string;
  onSource?: () => void;
  /** Cifra pendiente: se pinta más pequeña y en ámbar. */
  pending?: boolean;
}

/**
 * El panel oscuro de cabecera de un informe: cuatro cifras grandes, cada una
 * con su pista y, si hace falta, su procedencia. Las cifras cuentan desde
 * cero cuando el panel entra en pantalla.
 */
export function HeroTiles({ title, chip, chipTone = 'accent', tiles, className }: {
  title: string;
  chip?: string;
  chipTone?: 'accent' | 'warn';
  tiles: HeroTile[];
  className?: string;
}) {
  // Las cifras llegan después del montaje en Insights, y cambian con la
  // selección: la rampa vuelve a contar cada vez que cambian.
  const { ref, p } = useScrollRamp(800, tiles.map((k) => k.number ?? k.value).join('|'));
  return (
    <section
      ref={ref}
      className={cn('flex flex-col gap-5 rounded-[18px] p-[22px]', className)}
      style={{ background: 'var(--color-dash-night)' }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-base font-medium text-white">{title}</h3>
        {chip && (
          <span
            className="dash-mono rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em]"
            style={
              chipTone === 'warn'
                ? { background: 'rgba(251,191,36,0.16)', color: 'var(--color-dash-amber)' }
                : { background: 'rgba(200,229,48,0.14)', color: 'var(--color-dash-lime)' }
            }
          >
            {chip}
          </span>
        )}
      </div>
      <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(190px,1fr))]">
        {tiles.map((k) => {
          const Tag = k.onSource ? 'button' : 'div';
          const accent = k.tone === 'accent';
          const warn = k.tone === 'warn';
          const shown = k.number != null && k.format ? k.format(Math.round(k.number * p)) : k.value;
          const labelFg = accent ? 'var(--color-dash-lime)' : warn ? 'var(--color-dash-amber)' : 'var(--color-dash-night-fg-muted)';
          const sourceFg = warn ? 'var(--color-dash-amber)' : 'var(--color-dash-lime)';
          return (
            <Tag
              key={k.label}
              type={k.onSource ? 'button' : undefined}
              onClick={k.onSource}
              className={cn('flex flex-col gap-1 rounded-[14px] border p-[18px] text-left', k.onSource && 'cursor-pointer')}
              style={{
                background: accent ? 'var(--color-dash-night-3)' : 'var(--color-dash-night-2)',
                borderColor: accent ? 'var(--color-dash-night-border-lime)' : warn ? '#4a3a12' : 'var(--color-dash-night-border)',
              }}
            >
              <span className="dash-mono text-[11px] font-bold uppercase tracking-[0.1em]" style={{ color: labelFg }}>
                {k.label}
              </span>
              <span
                className="mt-1.5 font-bold leading-[1.05] tabular-nums"
                style={{ fontSize: k.pending ? 30 : 42, color: k.pending ? 'var(--color-dash-amber)' : '#ffffff' }}
              >
                {shown}
              </span>
              {k.hint && (
                <span className="text-xs leading-[1.45] [text-wrap:pretty]" style={{ color: 'var(--color-dash-night-fg-muted)' }}>
                  {k.hint}
                </span>
              )}
              {k.source && (
                <span className="mt-1.5 flex items-center gap-1.5 border-t pt-2" style={{ borderColor: 'var(--color-dash-night-border)' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={sourceFg} strokeWidth="2" aria-hidden>
                    <path d="M4 8l8-4 8 4v8l-8 4-8-4z" />
                  </svg>
                  <span className="dash-mono min-w-0 flex-1 text-[9px] font-bold uppercase tracking-[0.08em]" style={{ color: sourceFg }}>
                    {k.source}
                  </span>
                  <span className="text-[11px] font-bold" style={{ color: sourceFg }}>→</span>
                </span>
              )}
            </Tag>
          );
        })}
      </div>
    </section>
  );
}
